import { useRef, useState, type InputHTMLAttributes } from 'react';
import { IconEye, IconEyeOff } from './icons';

/**
 * A password field with a show/hide toggle (the eye at the right). Typing a long password,
 * like a generated one, is much easier when you can check it.
 */
export function PasswordInput({ className = 'input', ...props }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [visible, setVisible] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const toggle = () => {
    const el = input.current;
    const start = el?.selectionStart;
    const end = el?.selectionEnd;
    const typing = el != null && document.activeElement === el && start != null && end != null;
    setVisible((v) => !v);
    // Chrome moves the caret to the start of the field once it has finished handling a mouse
    // click that changed the field's type, so typing on after a peek would land at the front
    // of the password. Put the caret back after that.
    if (typing) {
      setTimeout(() => {
        if (document.activeElement === el) el.setSelectionRange(start, end);
      }, 0);
    }
  };

  return (
    <div className="password-field">
      <input {...props} ref={input} className={className} type={visible ? 'text' : 'password'} autoCapitalize="off" autoCorrect="off" spellCheck={false} />
      <button
        type="button"
        className="password-toggle"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
        // Keep the cursor in the field while toggling.
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
      >
        {visible ? <IconEyeOff size={18} /> : <IconEye size={18} />}
      </button>
    </div>
  );
}
